import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { MessageSquare, Filter, Download, Eye, FileText, Image, ExternalLink, Loader2 } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAdminFeedback, FeedbackType, FeedbackStatus, PlatformFeedback } from '@/hooks/usePlatformFeedback';
import { supabase } from '@/integrations/supabase/client';

const feedbackTypeConfig: Record<FeedbackType, { label: string; color: string }> = {
  bug_report: { label: 'Bug Report', color: 'bg-destructive/10 text-destructive border-destructive/20' },
  feature_request: { label: 'Feature Request', color: 'bg-warning/10 text-warning border-warning/20' },
  general_feedback: { label: 'General Feedback', color: 'bg-primary/10 text-primary border-primary/20' },
  other: { label: 'Other', color: 'bg-muted text-muted-foreground border-muted' },
};

const statusConfig: Record<FeedbackStatus, { label: string; color: string }> = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
  acknowledged: { label: 'Acknowledged', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
  wont_fix: { label: "Won't Fix", color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' },
};

interface UserInfo {
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}

export default function AdminFeedbackPage() {
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<FeedbackType | 'all'>('all');
  const [selectedFeedback, setSelectedFeedback] = useState<PlatformFeedback | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const { feedback, stats, loading, updateStatus, updateAdminNotes, getAttachmentUrl } = useAdminFeedback({
    status: statusFilter,
    type: typeFilter,
  });

  useEffect(() => {
    if (selectedFeedback) {
      setAdminNotes(selectedFeedback.admin_notes || '');
      loadUserInfo(selectedFeedback.user_id);
      loadAttachments(selectedFeedback);
    } else {
      setUserInfo(null);
      setAttachmentUrls({});
    }
  }, [selectedFeedback]);

  const loadUserInfo = async (userId: string) => {
    type ProfileResult = { data: { first_name: string | null; last_name: string | null } | null; error: unknown };
    
    try {
      // Try to get from client_profiles table
      const clientResult = await supabase
        .from('client_profiles')
        .select('first_name, last_name')
        .eq('user_id', userId)
        .maybeSingle();
      
      const clientProfile = (clientResult as unknown as ProfileResult).data;

      if (clientProfile?.first_name) {
        setUserInfo({
          email: '',
          firstName: clientProfile.first_name || undefined,
          lastName: clientProfile.last_name || undefined,
          role: 'Client',
        });
        return;
      }

      // Try vendor_profiles table
      const vendorResult = await supabase
        .from('vendor_profiles' as any)
        .select('first_name, last_name')
        .eq('user_id', userId)
        .maybeSingle();
      
      const vendorProfile = (vendorResult as unknown as ProfileResult).data;

      if (vendorProfile?.first_name) {
        setUserInfo({
          email: '',
          firstName: vendorProfile.first_name || undefined,
          lastName: vendorProfile.last_name || undefined,
          role: 'Vendor',
        });
        return;
      }

      setUserInfo({ email: 'Unknown user' });
    } catch (error) {
      console.error('Error loading user info:', error);
      setUserInfo({ email: 'Unknown user' });
    }
  };

  const loadAttachments = async (feedback: PlatformFeedback) => {
    if (!feedback.attachment_urls || feedback.attachment_urls.length === 0) return;

    setLoadingAttachments(true);
    const urls: Record<string, string> = {};

    for (const path of feedback.attachment_urls) {
      const url = await getAttachmentUrl(path);
      if (url) {
        urls[path] = url;
      }
    }

    setAttachmentUrls(urls);
    setLoadingAttachments(false);
  };

  const handleSaveNotes = async () => {
    if (!selectedFeedback) return;
    await updateAdminNotes(selectedFeedback.id, adminNotes);
  };

  const handleStatusChange = async (status: FeedbackStatus) => {
    if (!selectedFeedback) return;
    await updateStatus(selectedFeedback.id, status);
    setSelectedFeedback(prev => prev ? { ...prev, status } : null);
  };

  const getFileName = (path: string) => {
    const parts = path.split('/');
    return parts[parts.length - 1];
  };

  const isImage = (path: string) => {
    const ext = path.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png'].includes(ext || '');
  };

  return (
    <>
      <div className="container py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">User Feedback</h1>
          </div>
          <p className="text-muted-foreground">
            Review and manage feedback submitted by platform users.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>New</CardDescription>
              <CardTitle className="text-2xl text-blue-600">{stats.new}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Under Review</CardDescription>
              <CardTitle className="text-2xl text-yellow-600">{stats.under_review}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Acknowledged</CardDescription>
              <CardTitle className="text-2xl text-purple-600">{stats.acknowledged}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Resolved</CardDescription>
              <CardTitle className="text-2xl text-green-600">{stats.resolved}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total</CardDescription>
              <CardTitle className="text-2xl">{stats.total}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as FeedbackStatus | 'all')}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.entries(statusConfig).map(([value, config]) => (
                    <SelectItem key={value} value={value}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as FeedbackType | 'all')}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(feedbackTypeConfig).map(([value, config]) => (
                    <SelectItem key={value} value={value}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Feedback Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : feedback.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No feedback found matching the current filters.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Attachments</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedback.map((item) => {
                    const typeConfig = feedbackTypeConfig[item.feedback_type];
                    const status = statusConfig[item.status];
                    const hasAttachments = item.attachment_urls && item.attachment_urls.length > 0;

                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="max-w-[300px]">
                            <p className="font-medium truncate">{item.subject}</p>
                            <p className="text-sm text-muted-foreground truncate">
                              {item.message.substring(0, 100)}...
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={typeConfig.color}>
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={status.color}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(item.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>
                          {hasAttachments && (
                            <Badge variant="outline">
                              <FileText className="h-3 w-3 mr-1" />
                              {item.attachment_urls?.length}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedFeedback(item)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Detail Modal */}
        <Dialog open={!!selectedFeedback} onOpenChange={(open) => !open && setSelectedFeedback(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedFeedback && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className={feedbackTypeConfig[selectedFeedback.feedback_type].color}>
                      {feedbackTypeConfig[selectedFeedback.feedback_type].label}
                    </Badge>
                    <Badge variant="secondary" className={statusConfig[selectedFeedback.status].color}>
                      {statusConfig[selectedFeedback.status].label}
                    </Badge>
                  </div>
                  <DialogTitle>{selectedFeedback.subject}</DialogTitle>
                  <DialogDescription>
                    Submitted on {format(new Date(selectedFeedback.created_at), 'MMMM d, yyyy at h:mm a')}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                  {/* User Info */}
                  {userInfo && (
                    <div className="bg-muted/50 rounded-lg p-4">
                      <Label className="text-xs text-muted-foreground">Submitted by</Label>
                      <p className="font-medium">
                        {userInfo.firstName && userInfo.lastName 
                          ? `${userInfo.firstName} ${userInfo.lastName}`
                          : userInfo.email
                        }
                      </p>
                      <p className="text-sm text-muted-foreground">{userInfo.email}</p>
                      {userInfo.role && (
                        <Badge variant="outline" className="mt-2">{userInfo.role}</Badge>
                      )}
                    </div>
                  )}

                  {/* Message */}
                  <div>
                    <Label className="text-xs text-muted-foreground">Message</Label>
                    <div className="mt-2 p-4 bg-muted/50 rounded-lg whitespace-pre-wrap">
                      {selectedFeedback.message}
                    </div>
                  </div>

                  {/* Attachments */}
                  {selectedFeedback.attachment_urls && selectedFeedback.attachment_urls.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Attachments</Label>
                      {loadingAttachments ? (
                        <div className="flex items-center gap-2 mt-2 text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading attachments...
                        </div>
                      ) : (
                        <div className="mt-2 grid grid-cols-2 gap-4">
                          {selectedFeedback.attachment_urls.map((path, index) => (
                            <div key={index} className="border rounded-lg p-3">
                              {isImage(path) && attachmentUrls[path] && (
                                <img
                                  src={attachmentUrls[path]}
                                  alt={getFileName(path)}
                                  className="w-full h-32 object-cover rounded mb-2"
                                />
                              )}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0">
                                  {isImage(path) ? (
                                    <Image className="h-4 w-4 flex-shrink-0" />
                                  ) : (
                                    <FileText className="h-4 w-4 flex-shrink-0" />
                                  )}
                                  <span className="text-sm truncate">{getFileName(path)}</span>
                                </div>
                                {attachmentUrls[path] && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    asChild
                                  >
                                    <a href={attachmentUrls[path]} target="_blank" rel="noopener noreferrer">
                                      <ExternalLink className="h-4 w-4" />
                                    </a>
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Status Update */}
                  <div>
                    <Label htmlFor="status">Update Status</Label>
                    <Select
                      value={selectedFeedback.status}
                      onValueChange={(v) => handleStatusChange(v as FeedbackStatus)}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(statusConfig).map(([value, config]) => (
                          <SelectItem key={value} value={value}>{config.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Admin Notes */}
                  <div>
                    <Label htmlFor="notes">Internal Notes</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      These notes are only visible to administrators.
                    </p>
                    <Textarea
                      id="notes"
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add internal notes about this feedback..."
                      className="min-h-[100px]"
                    />
                    <Button
                      onClick={handleSaveNotes}
                      className="mt-2"
                      size="sm"
                    >
                      Save Notes
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
